// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : widgets wiki templates
// =============================================================================
// ---Third-party imports
import React from 'react'
import clsx from 'clsx'
import {isValid as isValidDate, parseISO} from 'date-fns'

// ---Framework imports
import log from 'uix/logging'
import {keys, dict, filterItems, get, flatten} from 'ldash'
import {decodeAttribH5, hashObj} from 'uix/utils'
import {PageSetViewer} from 'uix/viewer/components'
import {GenericTagHandler} from 'uix/wiki/utils'

import {useActions, useMemo, useDebounce, useSelector} from 'uix/hooks'
import {useTopstructures} from 'uix/modules/navigation/hooks'

// ---Local imports
import {HeaderSliderProto} from './heroSlider'
import {LinkBox} from './linkBox'
import {Timeline} from './timeline'
import {useSearch} from './hooks'

// ---Exports
export {FancyHeader} from './header'
export {ExplorerHandler} from './explorer'
export {BannerHandler} from './banner'
export {SwiperHandler} from './swiper'

const TimelineEvents = ({events, ranges = [], ...props}) => {
  let luceneEventSource = events.filter(evts => evts.query)[0]
  let luceneQuery = get(luceneEventSource, 'query', '')
  let [bouncedQuery] = useDebounce(luceneQuery, 1000)
  let data = useSearch(bouncedQuery)

  let staticEventSources = events.filter(evts => evts.statics)
  let staticEvents = flatten(
    staticEventSources.map(evts =>
      evts.statics
        .filter(e => true || isValidDate(parseISO(e.date)))
        .map(e => ({...e, category: evts.category?.name}))
    )
  )
  data = useMemo(
    () => [...data.map(d => ({...d, category: luceneEventSource.category?.name})), ...staticEvents],
    [data, hashObj(staticEvents)]
  )
  // XXX - should be handled in Timeline
  if (data.length == 0) data = undefined
  return <Timeline data={data} ranges={ranges} {...props} />
}
export const TimelineHandler = GenericTagHandler(
  // XXX: clashes with modules/timeline
  'Timeline2',
  ({attrib, children}) => {
    let {granularity} = attrib

    /*
      <category name=cat2 color=blue active>Fotos</category>
      <events cat1 lucene="ancestors:438918 AND NOT date:zzz"/>
    */

    let categories = dict(
      children
        .filter(e => e.tag === 'category' && e.attrib.name)
        .map(e => [e.attrib.name, {label: e.text, ...decodeAttribH5(e.attrib)}])
    )
    let get_cat = attrib_names => {
      let filtered = attrib_names.filter(n => categories[n])
      if (filtered[0]) {
        return categories[filtered[0]]
      } // else undefined
    }
    let get_events = events_el =>
      events_el.children
        ? events_el.children
            .filter(e => e.tag === 'event' && e.attrib.date)
            .map(e => ({date: e.attrib.date, text: e.text, icon: e.attrib.icon}))
        : null

    let eventSources = children
      .filter(e => e.tag === 'events')
      .map(e => ({
        query: e.attrib.lucene,
        category: get_cat(keys(e.attrib)),
        statics: get_events(e),
      }))

    let ranges = children
      .filter(e => e.tag.toLowerCase() === 'range')
      .map(e => ({
        startDate: get(e.attrib, 'from', '1900'),
        endDate: get(e.attrib, 'until', '2000'),
        color: e.attrib.color,
        title: e.text,
        category: get_cat(keys(e.attrib))?.name,
      }))

    //~ log.x(eventSources)

    return (
      <TimelineEvents
        events={eventSources}
        ranges={ranges}
        categories={categories}
        granularity={granularity}
      />
    )
  },
  {granularity: ['decades', 'years', 'months']}
)

export const SliderHeader = {
  match: ({tag}) => tag == 'SliderHeader',
  build: ({attrib, applyTemplates, children}) => {
    let items = children
      .filter(({tag}) => tag == 'item')
      .map(({attrib, text, children}) => {
        let description = children.length > 0 ? [...applyTemplates(children), text] : text
        return {...decodeAttribH5(attrib), description, pageId: attrib.page}
      })
    return <HeaderSliderProto data={items} />
  },
}

export const LinkBoxHandler = {
  match: ({tag}) => tag == 'LinkBox',
  build: ({attrib, children}) => {
    let items = children.filter(({tag}) => tag == 'item').map(({attrib}) => decodeAttribH5(attrib))
    return <LinkBox items={items} />
  },
}

//~ let matchP = (name, {tag, children}) => tag == 'p' && children.some(e => e.tag == name)

//~ export const TeaserPool = {
//~ match: ({tag, children}) =>
//~ matchP('Slider', {tag, children}) || matchP('Showcase', {tag, children}),
//~ init: ({keys, ddata, data}) => {
//~ let poolItems = [...(keys.byTag['Slider'] || []), ...(keys.byTag['Showcase'] || [])]
//~ if (poolItems.length > 0) {
//~ log(poolItems.map(e => e.attrib.arg1))
//~ let otids = poolItems.map(e => e.attrib.arg1)
//~ return (
//~ <div style={{height: '400px', display: 'flex', overflow: 'hidden'}}>
//~ <PageSetViewer
//~ otids={otids}
//~ vid={`teaserpool_${data.otid}`}
//~ activeLayers={['crop']}
//~ flex
//~ />
//~ </div>
//~ )
//~ }
//~ },
//~ build: ({attrib, index}) => [null, null],
//~ completion: ({jobSpec}) => {
//~ return {'!top': ['TitleInfo'], TitleInfo: {attrs: {id: ['1', '2']}}}
//~ },
//~ }
